/**
 * Validation utilities for form inputs
 *
 * Provides comprehensive validation functions for percentage fields,
 * decimal inputs, and other common form validation scenarios.
 */

export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

const NUMBER_PATTERN = /^-?(?:\d+(?:\.\d+)?|\.\d+)$/;

const EMAIL_JUNK_TO_CLEAN =
  /^['"`.,;:<>()[\]{}|\\\u2018\u2019\u201C\u201D]+|['"`.,;:<>()[\]{}|\\\u2018\u2019\u201C\u201D]+$/g;

export function cleanEmail(email: string): string {
  return email
    .replace(/\s+/g, '')
    .replace(/^mailto:/i, '')
    .replace(EMAIL_JUNK_TO_CLEAN, '');
}

export function isValidEmail(email: string): boolean {
  return /^[^\s@"'`<>()[\]{},;:\\]+@[^\s@"'`<>()[\]{},;:\\.]+(?:\.[^\s@"'`<>()[\]{},;:\\.]+)+$/.test(
    email
  );
}

const PHONE_MIN_DIGITS = 7;

export function isValidPhone(phone: string): boolean {
  const digitCount = phone.replace(/\D/g, '').length;
  return /^\+?[0-9 \-()]{7,20}$/.test(phone) && digitCount >= PHONE_MIN_DIGITS;
}

export function getPhoneError(
  phone: string | null | undefined
): string | undefined {
  const trimmedPhone = phone?.trim() ?? '';
  if (trimmedPhone === '' || isValidPhone(trimmedPhone)) return undefined;
  return 'Please enter a valid phone number';
}

export function isValidWebsite(website: string): boolean {
  return /^https?:\/\/[^\s/?#]+\.[^\s/?#]+([/?#]\S*)?$/.test(website);
}

/**
 * Validates an IBAN with the mod-97 check the backend applies, so a bad IBAN
 * is caught in the form rather than coming back as a 422 on save.
 */
export function isValidIban(iban: string): boolean {
  const normalized = iban.replace(/\s+/g, '').toUpperCase();

  if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]{10,30}$/.test(normalized)) return false;

  const rearranged = normalized.slice(4) + normalized.slice(0, 4);
  const numeric = rearranged.replace(/[A-Z]/g, (letter) =>
    String(letter.charCodeAt(0) - 55)
  );

  // Chunked mod-97: the whole number overflows Number precision.
  let remainder = 0;
  for (const digit of numeric) {
    remainder = (remainder * 10 + Number(digit)) % 97;
  }

  return remainder === 1;
}

/**
 * Validates percentage values with range checking (0-100%)
 * @param value - The percentage value as string
 * @param options - Validation options
 */
interface PercentageValidationOptions {
  required?: boolean;
  min?: number;
  max?: number;
  allowNegative?: boolean;
  maxDecimals?: number;
}

export function validatePercentage(
  value: string | null | undefined,
  options: PercentageValidationOptions = {}
): ValidationResult {
  const {
    required = false,
    min = 0,
    max = 100,
    allowNegative = false,
    maxDecimals = 3,
  } = options;

  // Handle empty values
  if (!value || value.trim() === '') {
    if (required) {
      return { isValid: false, error: 'Required' };
    }
    return { isValid: true };
  }

  // Check for valid number format
  const trimmedValue = value.trim();
  if (!NUMBER_PATTERN.test(trimmedValue)) {
    return { isValid: false, error: 'Must be a valid number' };
  }

  const numValue = Number.parseFloat(trimmedValue);

  if (Number.isNaN(numValue)) {
    return { isValid: false, error: 'Must be a valid number' };
  }

  // Check negative values
  if (!allowNegative && numValue < 0) {
    return { isValid: false, error: 'Cannot be negative' };
  }

  if (numValue < min) {
    return { isValid: false, error: `Must be at least ${min}%` };
  }

  if (numValue > max) {
    return { isValid: false, error: `Must be at most ${max}%` };
  }

  const decimalPart = trimmedValue.split('.')[1];
  if (decimalPart && decimalPart.length > maxDecimals) {
    return {
      isValid: false,
      error: `Cannot have more than ${maxDecimals} decimal places`,
    };
  }

  return { isValid: true };
}

/**
 * Validates decimal inputs with precision and range checking
 */
interface DecimalValidationOptions {
  required?: boolean;
  min?: number;
  max?: number;
  maxDecimals?: number;
  allowNegative?: boolean;
}

export function validateDecimal(
  value: string | null | undefined,
  options: DecimalValidationOptions = {}
): ValidationResult {
  const {
    required = false,
    min,
    max,
    maxDecimals = 6,
    allowNegative = false,
  } = options;

  // Handle empty values
  if (!value || value.trim() === '') {
    if (required) {
      return { isValid: false, error: 'Required' };
    }
    return { isValid: true };
  }

  const trimmedValue = value.trim();

  // Check for valid decimal format
  if (!NUMBER_PATTERN.test(trimmedValue)) {
    return { isValid: false, error: 'Must be a valid decimal number' };
  }

  const numValue = Number.parseFloat(trimmedValue);

  // Check for NaN
  if (Number.isNaN(numValue)) {
    return { isValid: false, error: 'Must be a valid number' };
  }

  // Check negative values
  if (!allowNegative && numValue < 0) {
    return { isValid: false, error: 'Cannot be negative' };
  }

  // Check range
  if (min !== undefined && numValue < min) {
    return { isValid: false, error: `Must be at least ${min}` };
  }

  if (max !== undefined && numValue > max) {
    return { isValid: false, error: `Must be at most ${max}` };
  }

  // Check decimal places
  const decimalPart = trimmedValue.split('.')[1];
  if (decimalPart && decimalPart.length > maxDecimals) {
    return {
      isValid: false,
      error: `Cannot have more than ${maxDecimals} decimal places`,
    };
  }

  return { isValid: true };
}

/**
 * Validates number-typed inputs (form state is number | null, not string)
 * maxDecimals is intentionally not enforced here - NumberInput truncates
 * to decimalPlaces at input time, so the form never produces over-precise values.
 */
export function validateNumber(
  value: number | null | undefined,
  options: DecimalValidationOptions = {}
): ValidationResult {
  const { required = false, min, max, allowNegative = false } = options;

  if (value == null) {
    if (required) return { isValid: false, error: 'Required' };
    return { isValid: true };
  }

  if (!Number.isFinite(value)) {
    return { isValid: false, error: 'Must be a valid number' };
  }

  if (!allowNegative && value < 0) {
    return { isValid: false, error: 'Cannot be negative' };
  }

  if (min !== undefined && value < min) {
    return { isValid: false, error: `Must be at least ${min}` };
  }

  if (max !== undefined && value > max) {
    return { isValid: false, error: `Must be at most ${max}` };
  }

  return { isValid: true };
}

/**
 * Validates management fee percentage (typically 0-5%)
 */
export function validateManagementFeePercentage(
  value: string | null | undefined
): ValidationResult {
  return validatePercentage(value, {
    min: 0,
    max: 100, // Allow up to 100% for flexibility
    maxDecimals: 3,
  });
}

/**
 * Validates hurdle rate percentage (typically 0-20%)
 */
export function validateHurdleRatePercentage(
  value: string | null | undefined
): ValidationResult {
  return validatePercentage(value, {
    min: 0,
    max: 100, // Allow up to 100% for flexibility
    maxDecimals: 3,
  });
}

/**
 * Validates carried interest percentage (typically 0-30%)
 */
export function validateCarriedInterestPercentage(
  value: string | null | undefined
): ValidationResult {
  return validatePercentage(value, {
    min: 0,
    max: 100, // Allow up to 100% for flexibility
    maxDecimals: 3,
  });
}

/**
 * Formats a number input to ensure proper decimal formatting
 * @param value - Input value
 * @param maxDecimals - Maximum decimal places allowed
 */
export function formatDecimalInput(
  value: string,
  maxDecimals: number = 6
): string {
  if (!value) return '';

  // Remove any non-numeric characters except decimal point and minus sign
  let cleaned = value.replace(/[^-0-9.]/g, '');

  // Ensure only one decimal point
  const parts = cleaned.split('.');
  if (parts.length > 2) {
    cleaned = parts[0] + '.' + parts.slice(1).join('');
  }

  // Ensure minus sign is only at the beginning and handle multiple minus signs
  if (cleaned.includes('-')) {
    const minusCount = (cleaned.match(/-/g) || []).length;
    const hasValidLeadingMinus =
      cleaned.startsWith('-') &&
      minusCount === 1 &&
      cleaned.indexOf('-', 1) === -1;

    // Remove all minus signs
    cleaned = cleaned.replaceAll('-', '');

    // Add back single minus at beginning only if it was a single, leading minus
    if (hasValidLeadingMinus) {
      cleaned = '-' + cleaned;
    }
  }

  // Limit decimal places
  if (cleaned.includes('.')) {
    const [integerPart, decimalPart] = cleaned.split('.');
    if (decimalPart.length > maxDecimals) {
      cleaned = integerPart + '.' + decimalPart.substring(0, maxDecimals);
    }
  }

  return cleaned;
}

/**
 * Formats a percentage input to ensure proper formatting
 * @param value - Input value
 * @param maxDecimals - Maximum decimal places allowed
 */
export function formatPercentageInput(
  value: string,
  maxDecimals: number = 3
): string {
  return formatDecimalInput(value, maxDecimals);
}

/**
 * Real-time validation for form fields
 * Returns both validation result and formatted value
 */
interface ValidationFormatResult extends ValidationResult {
  formattedValue: string;
}

export function validateAndFormatPercentage(
  value: string,
  options: PercentageValidationOptions = {}
): ValidationFormatResult {
  const formattedValue = formatPercentageInput(value, options.maxDecimals);
  const validation = validatePercentage(formattedValue, options);

  return {
    ...validation,
    formattedValue,
  };
}

/**
 * Validation helpers for specific LPA terms fields
 */
export const lpaTermsValidation = {
  managementFee: (value: string | null | undefined) =>
    validateManagementFeePercentage(value),

  hurdleRate: (value: string | null | undefined) =>
    validateHurdleRatePercentage(value),

  carriedInterest: (value: string | null | undefined) =>
    validateCarriedInterestPercentage(value),

  formatManagementFee: (value: string) =>
    validateAndFormatPercentage(value, { min: 0, max: 100, maxDecimals: 3 }),

  formatHurdleRate: (value: string) =>
    validateAndFormatPercentage(value, { min: 0, max: 100, maxDecimals: 3 }),

  formatCarriedInterest: (value: string) =>
    validateAndFormatPercentage(value, { min: 0, max: 100, maxDecimals: 3 }),
};

/**
 * Returns true only for URLs safe to embed in an outgoing email link.
 * Allows http(s) and mailto; rejects javascript:, data:, file:, and
 * scheme-less strings.
 */
export function isSafeLinkUrl(url: string): boolean {
  return /^(https?:\/\/|mailto:)/i.test(url.trim());
}
